import classnames from 'classnames';
import React, { useMemo } from 'react';
import { isEmpty } from 'validate.js';

import UntypedBreedAvailability from 'dpl/breeder_search_results/components/BreedAvailability';
import CardImagePlaceholder from 'dpl/breeder_search_results/components/CardImagePlaceholder';
import { getBreederProfileUrl } from 'dpl/breeder_search_results/mobile/components/Card';
import UntypedCardMediaCarousel from 'dpl/breeder_search_results/mobile/components/CardMediaCarousel';
import useCurrentUser from 'dpl/common/hooks/useCurrentUser';
import { MOBILE_LAYOUTS } from 'dpl/components/BreederProfile/utils/constants';
import {
  ANALYTICS_EVENTS,
  ANALYTICS_VIEW_FEATURES,
  ANALYTICS_VIEW_FEATURE_LOCATIONS
} from 'dpl/constants/analytics';
import { MOBILE_LAYOUT } from 'dpl/constants/query_params';
import FavoriteButton from 'dpl/favorites/components/FavoriteButton';
import { FOLLOWABLE_TYPES } from 'dpl/favorites/utils/constants';
import UserAvatar from 'dpl/path_to_payment/components/UserAvatar';
import { SimilarBreedsSeeAllBreederRowResource } from 'dpl/puppy_search_results/constants/resource_types';
import { isNumeric } from 'dpl/shared/form/utils';
import { setAnalyticsOrigin } from 'dpl/shared/utils/setAnalyticsOrigin';
import { sendRudderstackEvent } from 'dpl/util/analytics';

const PHOTO_SIZE = 240;

const SMART_IMAGE_PROPS = {
  Tag: 'div',
  className: 'bg-center cover',
  crop: true,
  loadingClass: 'bg-light-gray',
  height: `${PHOTO_SIZE}px`,
  width: `${PHOTO_SIZE}px`,
  allowContextMenu: true
};

// The card's text gutter, in px - `ph5` on the same scale. The strip starts one
// gutter in so the first photo lines up with the copy beneath it, and still
// bleeds off the right so it reads as continuing past the viewport.
const STRIP_INSET = 20;

const SPLIDE_OPTIONS = { padding: { left: STRIP_INSET, right: 16 } };

const AVAILABILITY_SPLIDE_OPTIONS = { padding: STRIP_INSET };

/**
 * Both of these are `React.memo`s in .jsx files, so TypeScript infers no props
 * for them and rejects every one we pass. Naming what we use restores checking
 * at these call sites.
 */
const CardMediaCarousel = UntypedCardMediaCarousel as React.ComponentType<{
  mediaFiles: { id: number | string; url: string; alt: string }[];
  smartImageProps: Record<string, unknown>;
  splideOptions?: Record<string, unknown>;
  analytics?: Record<string, unknown>;
}>;

const BreedAvailability = UntypedBreedAvailability as React.ComponentType<{
  className?: string | null;
  breedName?: string | null;
  availablePuppyCount: number;
  availableAdultDogsCount: number;
  formattedUpcomingLittersDueDates: string[];
  splideOptions?: Record<string, unknown> | null;
  isLoading?: boolean;
}>;

interface SimilarBreedsSeeAllBreederCardProps {
  breeder: SimilarBreedsSeeAllBreederRowResource;
  position: number;
  page: number;
  view: string;
  className?: string | null;
}

function locationDescription(
  distanceInMiles: number | null,
  facilityLocation: string | null
) {
  const distance = isNumeric(distanceInMiles)
    ? `${distanceInMiles} ${distanceInMiles === 1 ? 'mile' : 'miles'} away`
    : null;

  return [distance, facilityLocation && `in ${facilityLocation}`]
    .filter(Boolean)
    .join(' ');
}

/**
 * One card of the "See all" overlay's breeders tab.
 *
 * Fed entirely from the `see_all` payload rather than reusing the main search's
 * breeder card, which fetches its own photos and availability per card. On a
 * page of forty that would be eighty extra requests for data this response
 * already carries - and the main search's card stays untouched.
 */
export default function SimilarBreedsSeeAllBreederCard({
  breeder,
  position,
  page,
  view,
  className = null
}: SimilarBreedsSeeAllBreederCardProps) {
  const {
    breeder_puppy_count: breederPuppyCount,
    distance_in_miles: distanceInMiles,
    breeder_breed_id: breederBreedId,
    description,
    upcoming_litter_due_date: upcomingLitterDueDate,
    photographs,
    breed,
    breeder_profile: breederProfile
  } = breeder;

  const {
    id: breederProfileId,
    user_id: userId,
    initials,
    name,
    display_name: displayName,
    first_name: firstName,
    facility_location: facilityLocation,
    profile_photo_url: profilePhotoUrl,
    cropped_photo_url: croppedPhotoUrl,
    url
  } = breederProfile;

  const { isBuyer, isLoggedIn } = useCurrentUser();

  const mediaFiles = useMemo(
    () =>
      (photographs || []).map(photo => ({
        ...photo,
        alt: photo.alt_text || `${breed?.name} puppy raised by ${name}`
      })),
    [photographs, breed, name]
  );

  // Everything that describes *which* result this is, with no `view_feature`:
  // `FavoriteButton` spreads what it is given over its own
  // `favorite_heart_icon`, so a `view_feature` in here would relabel the heart
  // as whatever the card is. The heart is its own feature, and the spec pins
  // it - the card names itself below instead.
  const resultAnalytics = {
    view,
    view_feature_location: ANALYTICS_VIEW_FEATURE_LOCATIONS.SEARCH_RESULTS,
    breeder_profile_id: breederProfileId,
    breed_id: breed?.id,
    page_number: page,
    position
  };

  const analytics = {
    ...resultAnalytics,
    view_feature: ANALYTICS_VIEW_FEATURES.BREEDER_CARD
  };

  function handleClick() {
    // A contact is made on the profile this leads to and reads the origin back
    // out of local storage there, so the click is what has to write it. Unlike
    // the module's card, this page is a view of its own and names itself.
    setAnalyticsOrigin({
      originView: view,
      originViewFeature: ANALYTICS_VIEW_FEATURES.BREEDER_CARD,
      originViewFeatureLocation: ANALYTICS_VIEW_FEATURE_LOCATIONS.SEARCH_RESULTS
    });

    sendRudderstackEvent(ANALYTICS_EVENTS.CARD_CLICKED_BY_USER, analytics);
  }

  const isFavoritable = !isLoggedIn || isBuyer;

  return (
    <a
      className={classnames(
        'SimilarBreedsSeeAllBreederCard db pv5 bb b--default bg-white',
        className
      )}
      href={getBreederProfileUrl(url, { [MOBILE_LAYOUT]: MOBILE_LAYOUTS.NEW })}
      onClick={handleClick}
      draggable="false"
    >
      <div className="overflow-hidden">
        {/* A breeder can qualify on a breed they have posted no photos of, and
            an empty carousel collapses to a bare gap. Same placeholder the main
            search's card falls back to, inset by `ml5` - the same 20px as
            `STRIP_INSET`, since the placeholder takes no style prop. */}
        {isEmpty(mediaFiles) ? (
          <CardImagePlaceholder className="ml5" size={`${PHOTO_SIZE}px`} />
        ) : (
          <CardMediaCarousel
            mediaFiles={mediaFiles}
            smartImageProps={SMART_IMAGE_PROPS}
            splideOptions={SPLIDE_OPTIONS}
            analytics={analytics}
          />
        )}
      </div>
      <div className="ph5 mt4 flex items-start justify-between gap-16">
        <div className="flex items-center gap-8 overflow-hidden">
          <UserAvatar
            className="flex-none"
            user={{
              id: userId,
              initials,
              profile_photo_url: croppedPhotoUrl || profilePhotoUrl
            }}
            size="xxm"
          />
          <div className="overflow-hidden">
            <p className="font-serif fw-medium font-18 lh-title truncate">
              Raised by {displayName}
            </p>
            <p className="font-14 secondary lh-title truncate">
              {locationDescription(distanceInMiles, facilityLocation)}
            </p>
          </div>
        </div>
        {/* A breeder with no `BreederBreed` for the badged breed has nothing
            to follow, so the heart has no subject. */}
        {isFavoritable && breederBreedId !== null && (
          <FavoriteButton
            analytics={resultAnalytics}
            followableTypeId={breederBreedId}
            followableType={FOLLOWABLE_TYPES.BREEDER_BREED}
            checkedIconProps={{ name: 'fetch-heart-full-breeder' }}
            uncheckedIconProps={{
              name: 'fetch-heart',
              className: 'secondary'
            }}
            breederFirstName={firstName}
            breederProfilePhotoUrl={profilePhotoUrl || undefined}
          />
        )}
      </div>
      {description && (
        <p className="ph5 mt3 font-14 secondary lh-body">{description}</p>
      )}
      <BreedAvailability
        className="mt4"
        breedName={breed?.name}
        availablePuppyCount={breederPuppyCount}
        // The payload counts puppies of the badged breed only; adult dogs are
        // not part of this selection.
        availableAdultDogsCount={0}
        formattedUpcomingLittersDueDates={
          upcomingLitterDueDate ? [upcomingLitterDueDate] : []
        }
        splideOptions={AVAILABILITY_SPLIDE_OPTIONS}
      />
    </a>
  );
}
